import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useAccountBanStore } from '../stores/account-ban.store';
import { issueRead } from '../stores/session-read';
import { redeemGoogleSignIn } from '../services/google-signin.service';
import type { User } from '../types';
import { toast } from 'react-toastify';

/**
 * Google OAuth Callback Page
 *
 * Web: the server's callback redirects here with NOTHING secret in the URL
 * and an httpOnly cookie holding a single-use code; this page POSTs it to
 * /auth/google/exchange for the token (google-signin.service). A `token` in
 * a web URL is ignored — nothing legitimate sends one any more, and honouring
 * it would let a crafted link sign a visitor into someone else's account.
 *
 * Native: the app signs in from its nvcapp:// listener (auth.store), and
 * only when the link carries the nonce this device minted
 * (services/google-app-nonce). Nothing routes a token here, so a `token` in
 * this page's URL is ignored on native too: it can only be a crafted one.
 */
function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleGoogleCallback = useAuthStore((state) => state.handleGoogleCallback);
  // The effect can re-run (StrictMode, a new navigate identity); the sign-in
  // must be completed once.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const error = searchParams.get('error');

    /* ===============================
       OAuth Error Handling
    =============================== */
    if (error) {
      let errorMessage = 'Google login failed. Please try again.';

      switch (error) {
        case 'oauth_failed':
          errorMessage = 'Google authentication failed. Please try again.';
          break;
        case 'user_creation_failed':
          errorMessage = 'Failed to create user account. Please try again.';
          break;
        case 'access_denied':
          errorMessage = 'Access denied. You need to allow access to continue.';
          break;
        default:
          errorMessage = 'Login failed. Please try again.';
      }

      useAuthStore.getState().setGoogleLoading(false);
      toast.error(errorMessage);
      void navigate('/login');
      return;
    }

    const complete = (token: string, user: User, isNewUser: boolean) => {
      // Update auth state. isNewUser means the backend CREATED the account on
      // this OAuth pass — the store fires the sign_up conversion off it.
      handleGoogleCallback(token, user, isNewUser);

      // The page change says it worked: no "now logged in" toast (David, 2026-10-29).
      void navigate('/', { replace: true });

      // Fallback: Use window.location as backup in case React Router is slow
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          console.warn('[GoogleCallback] Router redirect fallback triggered');
          window.location.href = '/';
        }
      }, 500);
    };

    /* ===============================
       Redeem the httpOnly sign-in code
    =============================== */
    // A ban answer is for the session this redeem began under (c-057).
    const sentUnder = issueRead();
    void redeemGoogleSignIn().then((outcome) => {
      if (outcome.kind === 'success') {
        complete(outcome.token, outcome.user, outcome.isNewUser);
        return;
      }

      useAuthStore.getState().setGoogleLoading(false);

      if (outcome.kind === 'banned') {
        // Ignored for a session that ended meanwhile: whoever is on screen now stays put.
        if (!useAccountBanStore.getState().suspend(outcome.payload, sentUnder)) return;
        void navigate('/login', { replace: true });
        return;
      }

      // Back-button return to this page after a completed sign-in: the code
      // is spent, but the visitor is already in.
      if (useAuthStore.getState().isAuthenticated) {
        void navigate('/', { replace: true });
        return;
      }

      toast.error(outcome.message);
      void navigate('/login', { replace: true });
    });
  }, [searchParams, navigate, handleGoogleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
          <img src="/logo.webp" alt="Loading" className="w-10 h-10 animate-pulse" />
        </div>
        <div className="loading loading-dots loading-lg text-primary mb-4"></div>
        <p className="text-sm text-base-content/60">Completing Google sign-in...</p>
      </div>
    </div>
  );
}

export default GoogleCallback;
